import * as React from 'react';
import { getImageUrl } from '@/utils/urls';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemSummary } from '@/types/item/ItemSummary';
import { useAppSelector } from '@/redux/hooks';

export type UseConsoleCarouselDataProps = {
    catalogId: number;
    initialIndex: number;
    itemId: number;
};

type Photos = {
    alt: string;
    src: string;
}[];

export type UseConsoleCarouselData = {
    canNext: boolean;
    canPrev: boolean;
    currentImageIndex: number;
    currentItem: ItemSummary;
    gotoNext: () => void;
    gotoPrevious: () => void;
    mappedPhotos: Photos;
};

const useConsoleCarouselData = ({
    catalogId,
    initialIndex,
    itemId,
}: UseConsoleCarouselDataProps): UseConsoleCarouselData => {
    const currentItem = useAppSelector((state) => getItemSummary(state, itemId));
    const [currentImageIndex, setCurrentImageIndex] = React.useState(initialIndex);
    const isPopout = initialIndex !== 0;

    React.useEffect(() => {
        // When the itemId changes, shift back to the first image
        if (!isPopout && itemId) {
            setCurrentImageIndex(0);
        }
    }, [itemId, isPopout]);

    const canPrev = currentImageIndex > 0;
    const canNext = currentImageIndex + 1 < currentItem.photos.length;

    const gotoNext = () => {
        canNext ? setCurrentImageIndex(currentImageIndex + 1) : null;
    };

    const gotoPrevious = () => {
        canPrev ? setCurrentImageIndex(currentImageIndex - 1) : null;
    };

    const mapSlidePhotos = React.useCallback(
        (imageNumber, index) => {
            const alt = index ? `${currentItem.title} - ${index + 1}` : currentItem.title;
            const imageUrl = getImageUrl({
                catalogId,
                imageNumber,
                imageVersion: currentItem.imageVersion,
                itemId,
                sellerId: currentItem.sellerId,
                width: 1024,
            });
            const mappedPhoto = {
                alt,
                src: imageUrl,
            };

            return mappedPhoto;
        },
        [catalogId, currentItem, itemId]
    );

    let mappedPhotos: Photos;

    if (currentItem.photos.length) {
        mappedPhotos = currentItem.photos.map(mapSlidePhotos);
    }

    return {
        canNext,
        canPrev,
        currentImageIndex,
        currentItem,
        gotoNext,
        gotoPrevious,
        mappedPhotos,
    };
};

export default useConsoleCarouselData;
